import {
  ButtonHTMLAttributes,
  forwardRef,
  MouseEvent,
  ReactElement,
  ReactNode,
} from "react";
import { cn } from "../../utils/classes";
import { Icon, IconType, isIconType } from "../icon";

export type ButtonVariant =
  | "gradient"
  | "primary"
  | "secondary"
  | "outline"
  | "text"
  | "link";
type ButtonSizeType = "xs" | "sm" | "md" | "lg";

export type ButtonProps = {
  size?: ButtonSizeType;
  className?: string;
  text?: string;
  variant?: ButtonVariant;
  children?: ReactNode;
  disabled?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  type?: HTMLButtonElement["type"];
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className = "",
      disabled = false,
      variant = "primary",

      onClick,
      size = "md",
      children,
      text,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        onClick={onClick}
        className={cn(
          `flex w-fit select-none items-center justify-center gap-2 text-sm font-semibold disabled:pointer-events-none`,
          "transition-colors duration-200 ease-in",
          // sizes
          size === "lg" && "h-12 rounded-xs p-4",
          size === "md" && "h-10 rounded-xs px-4 py-3",
          size === "sm" && "h-8 rounded-xxs px-3 py-2",
          size === "xs" && "h-6 rounded-xxs px-2 py-1 text-xs",

          // variants
          variant === "gradient" &&
            "cursor-pointer bg-gradient-to-r from-primary via-[#E93E62] to-secondary text-white hover:from-[#FF3B76] hover:to-[#FF792D] hover:opacity-90 enabled:active:from-[#CC2F5E] enabled:active:to-[#CC6124E5]/90 disabled:bg-gray-300 disabled:bg-none disabled:text-gray-500",
          variant === "primary" &&
            "bg-white text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:bg-gray-300 disabled:text-gray-500",
          variant === "secondary" &&
            "bg-gray-300 text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:bg-gray-300 disabled:text-gray-500",
          variant === "outline" &&
            "border border-gray-300 bg-white text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:border-none disabled:bg-gray-300 disabled:text-gray-500",
          variant === "text" &&
            "bg-transparent text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:text-gray-500",
          variant === "link" &&
            "bg-transparent text-gray-900 hover:underline enabled:active:underline disabled:text-gray-500",

          className,
        )}
        disabled={disabled}
        {...props}
      >
        {text && <span className="truncate text-nowrap">{text}</span>}
        {children}
      </button>
    );
  },
);

export type IconButtonProps = {
  icon: IconType | ReactElement;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "gradient" | "primary" | "secondary" | "outline" | "text";
  className?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  disabled?: boolean;
  title?: string;
};

export const IconButtonV2 = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      size = "sm",
      variant = "primary",
      onClick,
      className,
      type = "button",
      disabled,
      ...props
    }: IconButtonProps,
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "group/icon-button relative enabled:cursor-pointer disabled:pointer-events-none overflow-hidden",
          "transition-colors duration-200 ease-in",
          size === "xs" && "size-6 rounded-xxs p-1",
          size === "sm" && "size-8 rounded-xxs p-2",
          size === "md" && "size-10 rounded-xs p-3",
          size === "lg" && "size-12 rounded-xs p-4",
          // variants
          variant === "gradient" &&
            "bg-gradient-to-r from-primary via-[#E93E62] to-secondary text-white hover:from-[#FF3B76] hover:to-[#FF792D] hover:opacity-90 enabled:active:from-[#CC2F5E] enabled:active:to-[#CC6124E5]/90 disabled:bg-gray-200 disabled:bg-none disabled:text-gray-500",
          variant === "primary" &&
            "bg-white text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:bg-gray-300 disabled:text-gray-500",
          variant === "secondary" &&
            "bg-gray-300 text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:bg-gray-300 disabled:text-gray-500",
          variant === "outline" &&
            "border border-gray-300 bg-white text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:border-none disabled:bg-gray-300 disabled:text-gray-500",
          variant === "text" &&
            "bg-transparent text-gray-900 hover:bg-gray-400 enabled:active:bg-gray-500 disabled:text-gray-500",
          className,
        )}
        onClick={onClick}
        disabled={disabled}
        {...props}
      >
        {isIconType(icon) ? <Icon type={icon} className="size-full" /> : icon}
        <div className="group-active/icon-button:ripple pointer-events-none absolute left-1/2 top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 opacity-0 backdrop-brightness-125"></div>
      </button>
    );
  },
);
